import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import Styled from "@ats/src/views/sessions/components/Styled";
import NewHeaderBar from "@ats/src/views/sessions/components/NewHeaderBar";

interface SessionsLayoutProps {
  children: React.ReactNode;
  /**
   * Optional promo panel shown beside the form from xl (1024) up. When omitted
   * the layout is unchanged, which is what every session view other than Auth
   * relies on.
   *
   * Above the split the gradient header bar is not rendered at all — the promo
   * runs full bleed to the top of its column. Below the split the promo is
   * hidden and the header bar is present exactly as on every other view.
   */
  promo?: React.ReactNode;
}

function SessionsLayout({ children, promo }: SessionsLayoutProps) {
  if (promo == undefined) {
    return (
      <Styled.Container>
        <NewHeaderBar />
        <Styled.UI>{children}</Styled.UI>
      </Styled.Container>
    );
  }

  return (
    <Styled.Container>
      <Local.HeaderSlot>
        <NewHeaderBar />
      </Local.HeaderSlot>
      <Local.Split>
        <Local.PromoColumn>{promo}</Local.PromoColumn>
        <Local.FormColumn>{children}</Local.FormColumn>
      </Local.Split>
    </Styled.Container>
  );
}

export default SessionsLayout;

/* Styled Components
======================================================= */
let Local: any;
Local = {};

Local.HeaderSlot = styled.div((props: any) => {
  const t: any = props.theme;

  return css`
    label: SessionsLayout_HeaderSlot;
    display: block;

    ${t.mq["xl"]} {
      display: none;
    }
  `;
});

Local.Split = styled.div((props: any) => {
  const t: any = props.theme;

  return css`
    label: SessionsLayout_Split;
    display: flex;
    flex-direction: column;
    width: 100%;
    /* Matches Styled.UI: the header bar is 46px and is only present below xl. */
    height: calc(100% - 46px);

    ${t.mq["xl"]} {
      flex-direction: row;
      align-items: stretch;
      height: 100%;
    }
  `;
});

Local.PromoColumn = styled.div((props: any) => {
  const t: any = props.theme;

  return css`
    label: SessionsLayout_PromoColumn;
    display: none;

    ${t.mq["xl"]} {
      position: relative;
      display: block;
      flex: 0 1 50%;
      min-width: 0;
      /* Above xxl every extra pixel goes to the form side. */
      max-width: 720px;
    }

    /* The panel fills the column absolutely so it never contributes height. */
    > * {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
    }
  `;
});

Local.FormColumn = styled.div((props: any) => {
  const t: any = props.theme;

  return css`
    label: SessionsLayout_FormColumn;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    align-items: center;
    overflow-y: auto;

    ${t.mq["xl"]} {
      flex: 1 1 50%;
      min-width: 0;
      width: auto;
      background-color: ${t.dark ? "#0a0a0a" : t.color.white};
    }
  `;
});
